import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Sentry } from '../lib/sentry';
import { reloadForStaleDeploy } from '../lib/deployRecovery';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  private isChunkLoadError(error: Error): boolean {
    const msg = error.message || '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      (error.name === 'TypeError' && msg.includes('importing a module'))
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.isChunkLoadError(error)) {
      reloadForStaleDeploy();
      return;
    }
    console.error('RouteErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleRetry = () => {
    if (this.state.error && this.isChunkLoadError(this.state.error)) {
      reloadForStaleDeploy(true);
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null });
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center py-16 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-5">
              <AlertCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              This page hit an error
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-5 text-sm">
              Something went wrong loading this page. The rest of the app is unaffected.
            </p>

            {this.state.error && (
              <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 mb-5 text-left overflow-hidden">
                <p className="text-xs text-gray-700 dark:text-gray-300 font-mono break-words">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleGoBack}
                className="flex-1 flex items-center gap-2 justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </button>
              <button
                onClick={this.handleRetry}
                className="flex-1 flex items-center gap-2 justify-center bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Resets the error boundary when the route changes so a crash on one page does not block the next. */
export function KeyedRouteErrorBoundary({ children }: Props) {
  const location = useLocation();
  return <RouteErrorBoundary key={location.pathname}>{children}</RouteErrorBoundary>;
}
